import React, { useId } from 'react';
import MuiDrawer from '@mui/material/Drawer';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { styled } from '@mui/material/styles';
import { dsV2 } from '../../ds-v2-tokens';

/**
 * Drawer (slide-over droite) du Design System V2 (TEC-3051) — valeurs exactes de la maquette.
 *
 * Overlay `rgba(33,33,33,.3)` + flou, fermeture Échap/clic extérieur (Modal MUI), piège de
 * focus natif, `role="dialog"` + `aria-modal`. En-tête (titre Poppins Bold 15px + sous-titre
 * + croix 30px), corps défilant (padding 20), pied collant. Plein écran ≤ 96vw.
 */
export type DrawerV2Props = {
  open: boolean;
  onClose: () => void;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  children: React.ReactNode;
  footer?: React.ReactNode;
  /** Largeur du panneau (desktop). Défaut 460px. */
  width?: number;
};

const Panel = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  backgroundColor: dsV2.color.blanc,
  textAlign: 'left',
});

const Headerbar = styled(Box)({
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: 10,
  padding: '18px 20px 14px',
  borderBottom: `1px solid ${dsV2.color.border}`,
  flexShrink: 0,
});

const Content = styled(Box)({ flex: 1, overflowY: 'auto', padding: 20 });

const Footerbar = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: 10,
  padding: '14px 20px',
  borderTop: `1px solid ${dsV2.color.border}`,
  flexShrink: 0,
});

const DrawerV2 = ({ open, onClose, title, subtitle, children, footer, width = 460 }: DrawerV2Props) => {
  const titleId = useId();
  return (
    <MuiDrawer
      anchor="right"
      open={open}
      onClose={onClose}
      slotProps={{
        backdrop: { sx: { backgroundColor: 'rgba(33,33,33,0.3)', backdropFilter: 'blur(2px)' } },
        paper: {
          role: 'dialog',
          'aria-modal': true,
          'aria-labelledby': titleId,
          sx: {
            width: `min(${width}px, 96vw)`,
            maxWidth: '100vw',
            boxShadow: dsV2.shadow.lg,
            backgroundColor: dsV2.color.blanc,
          },
        },
      }}
    >
      <Panel>
        <Headerbar>
          <Box sx={{ minWidth: 0 }}>
            <Box
              id={titleId}
              sx={{
                fontFamily: dsV2.font.sansBold,
                fontWeight: 700,
                fontSize: 15,
                color: dsV2.color.noir,
                marginBottom: '3px',
              }}
            >
              {title}
            </Box>
            {subtitle ? (
              <Box
                sx={{
                  fontFamily: dsV2.font.sans,
                  fontSize: '11.5px',
                  color: dsV2.color.t4,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  flexWrap: 'wrap',
                }}
              >
                {subtitle}
              </Box>
            ) : null}
          </Box>
          <IconButton
            aria-label="Fermer"
            onClick={onClose}
            disableRipple
            sx={{
              width: 30,
              height: 30,
              borderRadius: '7px',
              backgroundColor: dsV2.color.surface2,
              border: `1px solid ${dsV2.color.border}`,
              color: dsV2.color.t3,
              flexShrink: 0,
              '&:hover': {
                backgroundColor: dsV2.color.rougeDim,
                color: dsV2.color.rouge,
                borderColor: dsV2.color.rougeBrd,
              },
            }}
          >
            <CloseRoundedIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Headerbar>
        <Content>{children}</Content>
        {footer ? <Footerbar>{footer}</Footerbar> : null}
      </Panel>
    </MuiDrawer>
  );
};

export default DrawerV2;
export { DrawerV2 };
